import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Security Note: Token Storage
 * 
 * Current implementation uses localStorage for JWT token storage.
 * This is a common pattern for SPAs but has XSS vulnerability considerations.
 * 
 * For enhanced security, consider:
 * 1. HttpOnly cookies (requires backend CORS/cookie configuration)
 * 2. Token refresh rotation
 * 3. Short token expiry with refresh tokens
 * 
 * Current mitigations:
 * - CSP headers to prevent XSS
 * - Token cleared on logout
 * - Auth state validated on each page load
 */
const TOKEN_KEY = 'auth_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Logout function defined early for use in checkAuth
  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      console.error('Error clearing token:', e);
    }
    setToken(null);
    setUser(null);
    setPermissions([]);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`);
          setUser(response.data);
          setPermissions(response.data.permissions || []);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, logout]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });
      
      const { access_token, user: userData } = response.data;
      
      try {
        localStorage.setItem(TOKEN_KEY, access_token);
      } catch (e) {
        console.error('Error storing token:', e);
      }
      setToken(access_token);
      setUser(userData);
      setPermissions(userData.permissions || []);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // Admin has all permissions (check both old and new admin role ids)
    if (user.role_ids?.includes('role-admin') || user.role_ids?.includes('admin')) return true;
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some(p => hasPermission(p));
  };

  const isAdmin = () => {
    return user?.role_ids?.includes('role-admin') || user?.role_ids?.includes('admin');
  };

  const value = {
    user,
    token,
    loading,
    permissions,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
